import { GailProductRoles } from "@/enums/user";
import { GalactusRole } from "@/types/authz";

/** Friendlier labels for built-in roles; custom role names are shown verbatim. */
export const STANDARD_ROLE_LABELS: Record<string, string> = {
  ReportUser: "Report User",
  EnterpriseManager: "Enterprise Manager",
  GailGPTUser: "GailGPT User",
  GailAgentUser: "Gail Agent User",
};

// Own-key check, not `?? `: a custom role may be named `constructor`/`toString`, and an inherited
// Object.prototype member would resolve to a function where every caller expects a string.
export const roleLabel = (authRoleName: string): string =>
  Object.hasOwn(STANDARD_ROLE_LABELS, authRoleName)
    ? STANDARD_ROLE_LABELS[authRoleName]
    : authRoleName;

const ADMIN_ROLE_NAME = "Admin";

/** Admin is the lockout backstop: its scopes are never editable and it is never deletable. */
export const isAdminRole = (authRoleName: string): boolean =>
  authRoleName.toLowerCase() === ADMIN_ROLE_NAME.toLowerCase();

/**
 * Product/seat roles stay on the Auth0 product path — never shown as base roles.
 *
 * `ReportUser` stays here so the Product Access checkbox remains the seat-gated
 * grant path. Assign/unassign goes through Galactus `user_roles` (Auth0 + mirror);
 * it is not a Galactus base-role multi-select value.
 */
export const PRODUCT_SEAT_ROLE_NAMES = new Set<string>([
  GailProductRoles.GailGPTUser,
  GailProductRoles.GailAgentUser,
  GailProductRoles.ReportUser,
]);

/** True when a role name belongs on the Galactus base-role list rather than the Auth0 seat path. */
export const isBaseRoleName = (authRoleName: string): boolean =>
  !PRODUCT_SEAT_ROLE_NAMES.has(authRoleName);

/** Role ids whose catalog names are product/seat roles (Auth0 seat path). */
export const productSeatRoleIdSet = (roles: GalactusRole[]): Set<string> =>
  new Set(
    roles.filter((role) => PRODUCT_SEAT_ROLE_NAMES.has(role.authRoleName)).map((role) => role.id)
  );

/** Drop product/seat role ids using the roles catalog so only base roles remain. */
export const filterBaseRoleIds = (
  ids: string[] | undefined | null,
  roles: GalactusRole[]
): string[] => {
  const seatIds = productSeatRoleIdSet(roles);
  return (ids ?? []).filter((id) => !seatIds.has(id));
};

/** Order-insensitive equality for two string lists (role names, scopes). */
export const sameStringSet = (a: string[], b: string[]): boolean =>
  a.length === b.length &&
  [...a].sort((x, y) => x.localeCompare(y)).join("\u0000") ===
    [...b].sort((x, y) => x.localeCompare(y)).join("\u0000");

const ROLE_COLUMN_PRIORITY = ["admin", "user"];

const roleColumnRank = (authRoleName: string): number => {
  const rank = ROLE_COLUMN_PRIORITY.indexOf(authRoleName.toLowerCase());
  return rank === -1 ? ROLE_COLUMN_PRIORITY.length : rank;
};

/** Keep Admin and User leftmost, then sort standard-first and alphabetically. */
export const compareGalactusRoles = (a: GalactusRole, b: GalactusRole): number => {
  const priority = roleColumnRank(a.authRoleName) - roleColumnRank(b.authRoleName);
  if (priority !== 0) return priority;
  if (a.type !== b.type) return a.type === "standard" ? -1 : 1;
  return roleLabel(a.authRoleName).localeCompare(roleLabel(b.authRoleName));
};

/** Dropdown label for a role; standard roles are wrapped via `formatStandard`. */
export const galactusRoleOptionLabel = (
  role: GalactusRole,
  formatStandard: (name: string) => string
): string =>
  role.type === "standard"
    ? formatStandard(roleLabel(role.authRoleName))
    : roleLabel(role.authRoleName);
